import React, { useState } from 'react';

import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import { Close } from '@mui/icons-material';
import { useFormik } from 'formik';

import styles from './CompanyInformation.module.css';
import Button from '../../Buttons/Button/Button';
import FormField from '../../UpdatedFormUI/FormField/FormField';
import FormSelectField from '../../UpdatedFormUI/FormSelectField/FormSelectField';
import FormRichTextField from '../../FormUI/FormRichTextField/FormRichTextField';
import userAvatarPlaceholder from '../../../../assets/images/other/user_avatar_placeholder.png';
import { useNavigate } from 'react-router-dom';
import { updateUser } from '../../../../services/userService';
import { fetchUserByToken, fetchUserByTokenWithoutLoading } from '../../../../app/features/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useGetCompanyIndustriesHook } from '../../../../utils/utilityHooks';
import { EditIcon, VerifyBadge, VerifyGreenBadge } from '../../../../assets/images/icons';

function CompanyInformation({ employer }) {
    const user = useSelector(state => state.user.user);
    const [formLoading, setFormLoading] = useState(false);
    const [editMode, setEditMode] = useState(true)
    const [isEditing, setIsEditing] = useState(false);
    const [imagePreview, setImagePreview] = useState('');
    const [profileImage, setProfileImage] = useState(null);

    const userToken = useSelector(state => state.user.token);
    const dispatch = useDispatch()
    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: {
            company_name: user.company_name,
            company_description: user.company_description,
            company_size: user.company_size,
            company_website: user.company_website,
            profile_image: '',
            company_industries: user.company_industries ? user.company_industries.split(';').map(item => item.trim()) : [],
        },
        validationSchema: Yup.object({
            company_name: Yup.string().when('employer_status', {
                is: 'business',
                then: schema => schema.required(t('general.UI.required')),
                otherwise: schema => schema.notRequired()
            }),
        }),
        onSubmit: async (values) => {
            if (formLoading) return;
            setFormLoading(true);
            const formData = new FormData();
            formData.append('token', userToken);
            formData.append('company_name', values.company_name);
            formData.append('company_description', values.company_description);
            formData.append('company_size', values.company_size);
            formData.append('company_website', values.company_website);
            formData.append('company_industries', values.company_industries.join(';'));
            if (profileImage) {
                formData.append('profile_image', profileImage);
            }
            try {
                await updateUser(formData);
                await dispatch(fetchUserByTokenWithoutLoading(userToken));
                await setFormLoading(false);
                await setEditMode(!editMode);
                await setIsEditing(!isEditing);
                const target = document.querySelector('.formScroll');
                if (target) {
                    window.scrollTo({
                        top: target.offsetTop,
                        behavior: 'smooth'
                    });
                }
                await toast.success(t('edit_profile.profile_updated'));
            } catch (error) {
                toast.error(error.response?.data?.error || 'Something went wrong');
                if (error?.response?.data?.field) {
                    formik.setErrors({
                        [error.response.data.field]: error.response.data.error,
                    });
                }
            } finally {
                setFormLoading(false);
            }
        },
    });

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setProfileImage(file);
            // Create a preview URL for the selected image
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };
    const handleEditToggle = () => {
        setIsEditing(!isEditing);
        setEditMode(!editMode)
    };
    const companyIndustriesOptions = useGetCompanyIndustriesHook();
    const handleCompanyIndustriesChange = (value) => {
        formik.setFieldValue('company_industries', value);
    }
    const companySizeOptions = [
        { value: '1 - 10', label: '1 - 10' },
        { value: '10 - 100', label: '10 - 100' },
        { value: '100 - 1000', label: '100 - 1000' },
        { value: '1000+', label: '1000+' },
    ];
    return (

        !editMode ?
            <>
                <div className={`${styles.container} formScroll`} >
                    {isEditing &&
                        <Close
                            className={styles.mobileIcon}
                            style={{
                                cursor: "pointer"
                            }}
                            onClick={() => { handleEditToggle() }}
                        />
                    }
                    <div className={styles.firstColumn}>
                        <div className={styles.profileImageContainer}>
                            {isEditing &&
                                <>
                                    <img
                                        className={styles.profileImage}
                                        src={imagePreview || (user?.profile_image ? `${process.env.REACT_APP_UPLOADS_PATH}${user?.profile_image}` : userAvatarPlaceholder)}
                                        alt={`${user?.first_name} ${user?.last_name}`}
                                        onError={(e) => {
                                            e.target.onerror = null; // Prevent infinite loop
                                            e.target.src = userAvatarPlaceholder; // Set fallback image
                                        }}
                                    />
                                    <label >
                                        <img
                                            className={`${styles.onlineStatus} ${styles.uploadIcon}`}
                                            src={EditIcon}
                                            style={{
                                                backgroundColor: "white",
                                                height: 20,
                                                width: 20,
                                                cursor: 'pointer',
                                            }}
                                            alt={"DownloadIcon"}
                                        />
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            style={{ display: "none" }}
                                            className={styles.fileInput}
                                        />
                                    </label>
                                </>
                            }
                        </div>
                    </div>

                    <div className={styles.secondColumn}>
                        <div className={`${styles.title} ${styles.centerImg}`}>
                            {isEditing &&
                                <div className={styles.editNameContainer}>
                                    <div>
                                        <FormField
                                            name="company_name"
                                            type="text"
                                            label={t('edit_profile.employer.company_name')}
                                            hasBorder
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.company_name}
                                            error={formik.touched.company_name && formik.errors.company_name}
                                        />
                                    </div>

                                    <div>
                                        <FormField
                                            name="company_website"
                                            type="text"
                                            label={t('edit_profile.employer.company_website')}
                                            hasBorder
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.company_website}
                                            error={formik.touched.company_website && formik.errors.company_website}
                                        />
                                    </div>
                                    <div>
                                        <FormSelectField
                                            name="company_industries"
                                            label={t('edit_profile.employer.company_industries')}
                                            type="default"
                                            hasBorder
                                            isMulti
                                            options={companyIndustriesOptions}
                                            onChange={handleCompanyIndustriesChange}
                                            value={formik.values.company_industries}
                                            error={formik.touched.company_industries && formik.errors.company_industries}
                                        />
                                    </div>
                                    <div>
                                        <FormSelectField
                                            name="company_size"
                                            label={t('edit_profile.employer.company_size')}
                                            type="default"
                                            hasBorder
                                            options={companySizeOptions}
                                            onFormikChange={formik.handleChange}
                                            value={formik.values.company_size}
                                            error={formik.touched.company_size && formik.errors.company_size}
                                        />
                                    </div>
                                    <div>
                                        <FormRichTextField
                                            name="company_description"
                                            label={t('edit_profile.employer.company_description')}
                                            hasBorder
                                            onFormikChange={formik.handleChange}
                                            value={formik.values.company_description}
                                            error={formik.touched.company_description && formik.errors.company_description}
                                        />
                                    </div>
                                </div>
                            }
                            {user?.verification_status === 'approved' && (
                                <img src={VerifyBadge} alt="Verified" className={styles.image} />
                            )}
                        </div>


                        {isEditing &&
                            <div style={{ textAlign: 'center' }}>
                                <Button onClick={() => { formik.submitForm() }}>
                                    {formLoading ? t('general.UI.loading') : t('general.UI.save')}
                                </Button>
                            </div>
                        }
                    </div>

                    <div className={styles.thirdColumn}>
                        {isEditing &&
                            <Close
                                className={styles.crossIcon}
                                style={{
                                    cursor: "pointer"
                                }}
                                onClick={() => { handleEditToggle() }}
                            />
                        }
                    </div>
                </div>
            </>
            :
            <>


                <div className={styles.container}>
                    <div className={styles.secondColumn}>
                        {!editMode &&
                            <Close
                                className={styles.mobileIcon}
                                style={{
                                    cursor: "pointer"
                                }}
                                onClick={() => { setEditMode(!editMode) }}
                            />}
                        {editMode &&
                            <>
                                <div className={styles.title}>
                                    {t('profileSidebar.tabs.companyInformation')}
                                </div>
                                <div style={{ display: "flex", gap: '10px', margin: "20px 0", alignItems: "center" }}>
                                    <div style={{ height: "60px", width: "60px", borderRadius: "30px", border: '1px solid green', overflow: "hidden" }}>
                                        {user.profile_image ?
                                            <img
                                                src={process.env.REACT_APP_UPLOADS_PATH + user.profile_image}
                                                style={{ borderRadius: "30px", height: "100%", width: "100%" }}
                                                onError={(e) => {
                                                    e.target.onerror = null; // Prevent infinite loop
                                                    e.target.src = userAvatarPlaceholder; // Set fallback image
                                                }}
                                            />
                                            :
                                            <img
                                                src={userAvatarPlaceholder}
                                                alt="User avatar"
                                                style={{ borderRadius: "30px", height: "100%", width: "100%" }}
                                                onError={(e) => {
                                                    e.target.onerror = null; // Prevent infinite loop
                                                    e.target.src = userAvatarPlaceholder; // Set fallback image
                                                }}
                                            />
                                        }
                                    </div>
                                    <p>{user.company_name}</p>
                                    <img src={VerifyGreenBadge} alt={t('profileSidebar.tabs.companyInformation')} />
                                </div>
                                <div className={styles.aboutTitle}>
                                    {t('companyDetails.industry')}
                                </div>
                                <div className={styles.description}>
                                    {user?.company_industries?.split(";").map((item) => (
                                        " " + t(`general.company_industries.${item}`) + " "
                                    ))}
                                </div>

                                <div className={styles.aboutTitle}>
                                    {t('companyDetails.companySize')}
                                </div>
                                <div className={styles.description}>
                                    {user.company_size}
                                </div>

                                <div className={styles.aboutTitle}>
                                    {t('companyDetails.about')}
                                </div>
                                <div className={styles.description}>
                                    <div
                                        className={styles.description}
                                        dangerouslySetInnerHTML={{ __html: user.company_description }}
                                    ></div>
                                </div>

                            </>
                        }
                    </div>
                    <div className={styles.thirdColumn}  >
                        {!editMode ?
                            <Close
                                className={styles.crossIcon}
                                style={{ cursor: "pointer" }}
                                onClick={handleEditToggle}
                            /> :
                            <img
                                src={EditIcon}
                                onClick={handleEditToggle}
                                alt={t('personalInformation.edit')}
                                className={styles.editIcon}
                            />
                        }
                    </div>
                </div >
            </>

    );
}

export default CompanyInformation;
